import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import { forwardRef, InputHTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  indeterminate?: boolean;
}

function createCheckboxChangeEvent(
  checked: boolean,
  name: string | undefined,
  value: InputHTMLAttributes<HTMLInputElement>['value']
): React.ChangeEvent<HTMLInputElement> {
  const target = {
    checked,
    name: name ?? '',
    value: value == null ? '' : String(value),
    type: 'checkbox'
  } as HTMLInputElement;

  let defaultPrevented = false;
  let propagationStopped = false;

  return {
    target,
    currentTarget: target,
    type: 'change',
    bubbles: true,
    cancelable: true,
    get defaultPrevented() {
      return defaultPrevented;
    },
    eventPhase: 0,
    isTrusted: false,
    nativeEvent: new Event('change', { bubbles: true }),
    timeStamp: typeof performance !== 'undefined' ? performance.now() : Date.now(),
    persist() {},
    preventDefault() {
      defaultPrevented = true;
    },
    stopPropagation() {
      propagationStopped = true;
    },
    isDefaultPrevented() {
      return defaultPrevented;
    },
    isPropagationStopped() {
      return propagationStopped;
    }
  };
}

/**
 * Base Checkbox component matching CompliMind Design System (Figma)
 * Uses Tailwind config color tokens only
 * 14x14px, 2px border radius, 1px border, 1.17px checkmark stroke
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      disabled,
      indeterminate,
      className = '',
      onChange,
      readOnly,
      name,
      id,
      value,
      tabIndex,
      title,
      'aria-hidden': ariaHidden,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy
    },
    ref
  ) => {
    let boxBg = 'bg-white';
    let border = 'border-content-default';
    let checkColor = '#fff';
    if (disabled) {
      if (checked || indeterminate) {
        boxBg = 'bg-bluegray-300';
        border = 'border-bluegray-400';
        checkColor = '#94A3B8';
      } else {
        boxBg = 'bg-white';
        border = 'border-bluegray-400';
        checkColor = '#94A3B8';
      }
    } else if (checked || indeterminate) {
      boxBg = 'bg-secondary-blue';
      border = 'border-secondary-blue';
      checkColor = '#fff';
    } else {
      boxBg = 'bg-white';
      border = 'border-content-default';
      checkColor = '#fff';
    }

    return (
      <label
        className={`inline-flex items-center cursor-pointer select-none relative ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${className}`}
      >
        <BaseCheckbox.Root
          id={id}
          name={name}
          value={value == null ? undefined : String(value)}
          checked={checked}
          indeterminate={indeterminate}
          disabled={disabled}
          readOnly={readOnly}
          inputRef={ref}
          // An explicit undefined would override Base UI's default tabIndex and drop the checkbox from the Tab order
          {...(tabIndex === undefined ? {} : { tabIndex })}
          title={title}
          aria-hidden={ariaHidden}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          className={twMerge(
            'relative flex items-center justify-center w-[14px] h-[14px] rounded-[2px] border transition-colors duration-150 p-0',
            border,
            boxBg,
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
          style={{ minWidth: 14, minHeight: 14 }}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onCheckedChange={(next, eventDetails) => {
            eventDetails.event.stopPropagation();
            if (readOnly) return;
            onChange?.(createCheckboxChangeEvent(next, name, value));
          }}
        >
          <BaseCheckbox.Indicator keepMounted className="flex data-[unchecked]:hidden pointer-events-none">
            {indeterminate ? (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="6.25" width="8" height="1.5" rx="0.75" fill={checkColor} />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M3.5 7.5L6 10L10.5 4.5"
                  stroke={checkColor}
                  strokeWidth="1.17"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </BaseCheckbox.Indicator>
        </BaseCheckbox.Root>
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
